import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import MedicalCheck from '../assets/icons/Medicalcheck.svg';
import MedicalQuestionnaireHeader from './MedicalQuestionnaireHeader';
import api from '../services/api';
import { ENDPOINTS } from '../services/endpoints';
import {
  buildOptionsByQuestionIdFromFilledEnvelope,
  buildValidationByQuestionIdFromFilledEnvelope,
  extractAnswerRowsFromQuestionsPayload,
  mergePrefillAnswers,
  parseFilledMedicalQuestionnaireListFromEnvelope,
  parseMedicalQuestionnaireSaveValidationError,
  persistMedicalQuestionnaireCompletedOnClient,
  saveMedicalQuestionnaireStepAnswer,
  stepPersistSnapshotFromAnswers,
} from '../services/medicalQuestionnaire';
import { setMedicalQuestionnaireTotalSections } from '../store/medicalQuestionnaireProgressSlice';
import useMedicalQuestionnaireProgress from '../hooks/useMedicalQuestionnaireProgress';
import { TEST_HISTORY_PATH } from '../utils/signInRedirect';
import {
  maxSelectionLimitMessage,
  stepDetailInputVisible,
} from '../utils/medicalQuestionnaireChoiceOptions';
import {
  buildSectionsFromApi,
  flatStepsFromSections,
  mergeOptionsIntoQuestionnairePayload,
  mergeValidationIntoQuestionnairePayload,
} from '../utils/medicalQuestionnaireSections';
import {
  applyQuestionnaireProgressAfterSave,
  finalizeLastOptionalQuestionnaireStep,
  flushQuestionnaireProgressOnFlowExit,
  syncOptimisticQuestionnaireProgress,
} from '../utils/questionnaireProgressSync';
import {
  buildCompletedStepsFromAnswers,
  getSectionCompletionMap,
  getStepRequiredEmptyMessage,
  isOptionalStepWithoutInput,
} from '../utils/medicalQuestionnaireStepValidation';


function sanitizeNumericInput(value, allowDecimal = true) {
  const cleaned = String(value || '').replace(allowDecimal ? /[^0-9.]/g : /[^0-9]/g, '');
  if (!allowDecimal) return cleaned;
  const firstDotIndex = cleaned.indexOf('.');
  if (firstDotIndex === -1) return cleaned;
  return `${cleaned.slice(0, firstDotIndex + 1)}${cleaned.slice(firstDotIndex + 1).replace(/\./g, '')}`;
}



/** Figma numeric / free_text input: 335×60, 20px padding, 12px radius, #CBD5E0 stroke. */
function questionnaireInputClass(hasFieldError) {
  return `box-border block h-[60px] w-full rounded-[12px] border bg-white px-5 font-poppins text-sm text-[#2B3133] outline-none focus:border-[#119975] placeholder:text-[#A7B0B3] ${
    hasFieldError ? 'border-red-500' : 'border-[#CBD5E0]'
  }`;
}

/** Figma: segment height 5px, 4px radius; width scales on small screens, ~55.45px when space allows. */
const PROGRESS_SEGMENT_CLASS =
  'box-border h-[5px] w-full min-w-0 rounded-[4px]';

const QUESTIONNAIRE_FRAME_CLASS = 'mx-auto w-full max-w-[690px] shrink-0';

const QUESTIONNAIRE_WIDE_OPTIONS_CLASS =
  'mx-auto w-full max-w-[960px] lg:max-w-[1040px]';

const CHOICE_BUTTON_FOCUS_CLASS =
  'outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-[#119975]/35 focus-visible:ring-offset-2';

/**
 * Figma multi_choice indicator (outer ring always 24×24, fill #F8F8F8):
 * - Unselected: 1px inside stroke #CBD5E0.
 * - Selected: 1px inside Main Green stroke + 16×16 Main Green inner dot.
 */
function ChoiceOptionIndicator({ selected }) {
  return (
    <span
      className={`box-border flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full border bg-[#F8F8F8] ${
        selected ? 'border-[#119975]' : 'border-[#CBD5E0]'
      }`}
      aria-hidden="true"
    >
      {selected ? <span className="h-[16px] w-[16px] rounded-full bg-[#119975]" /> : null}
    </span>
  );
}

export default function MedicalQuestionnaireFlow() {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const navigate = useNavigate();
  const questionnaireCompleted = useSelector(
    (s) => s.user.medical_questionnaire_completed === true
  );
  const { percent: progressPct } = useMedicalQuestionnaireProgress();
  const initialPrefillRef = useRef(
    location?.state?.prefillAnswers && typeof location.state.prefillAnswers === 'object'
      ? { ...location.state.prefillAnswers }
      : {}
  );
  const resumeNextQuestionRef = useRef(
    typeof location.state?.nextQuestion === 'number' && location.state.nextQuestion > 0
      ? location.state.nextQuestion
      : null
  );
  const [sections, setSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [answers, setAnswers] = useState(() => ({ ...initialPrefillRef.current }));
  const [completedSteps, setCompletedSteps] = useState({});
  const [fieldError, setFieldError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastPersistedSnapshotByStepKeyRef = useRef({});
  const questionnaireFinalizeRef = useRef(false);
  const flowStateRef = useRef({});

  const initialStep = useMemo(() => {
    const filled = location.state?.nextQuestion;
    return typeof filled === 'number' && filled > 0 ? filled - 1 : 0;
  }, [location.state?.nextQuestion]);

  const [currentStep, setCurrentStep] = useState(initialStep);

  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  useEffect(() => {
    setFieldError('');
  }, [currentStep]);



  const steps = useMemo(
    () =>
      sections.flatMap((section, sectionIdx) =>
        section.questions.map((q, questionIdx) => ({
          ...q,
          sectionIdx,
          questionIdx,
          sectionTitle: section.title,
        }))
      ),
    [sections]
  );

  useEffect(() => {
    if (questionnaireCompleted) {
      navigate(TEST_HISTORY_PATH, { replace: true });
    }
  }, [questionnaireCompleted, navigate]);

  useEffect(() => {
    const controller = new AbortController();
    const loadQuestions = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const response = await api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.QUESTIONS, {
          signal: controller.signal,
        });
        let validationByQuestionId = {};
        let optionsByQuestionId = {};
        let filledRows = [];
        try {
          const filledResponse = await api.get(ENDPOINTS.MEDICAL_QUESTIONNAIRE.FILLED, {
            signal: controller.signal,
          });
          const filledEnvelope = filledResponse?.data;
          validationByQuestionId = buildValidationByQuestionIdFromFilledEnvelope(filledEnvelope);
          optionsByQuestionId = buildOptionsByQuestionIdFromFilledEnvelope(filledEnvelope);
          filledRows = parseFilledMedicalQuestionnaireListFromEnvelope(filledEnvelope);
        } catch (error) {
          const isRequestCancelled =
            controller.signal.aborted ||
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED';
          if (isRequestCancelled) throw error;
        }

        const json = mergeOptionsIntoQuestionnairePayload(
          mergeValidationIntoQuestionnairePayload(response?.data, validationByQuestionId),
          optionsByQuestionId
        );
        const mapped = buildSectionsFromApi(json);
        if (!mapped.length) throw new Error('No questionnaire data');

        const flatSteps = flatStepsFromSections(mapped);

        let merged = { ...initialPrefillRef.current };
        const combinedRows = [...filledRows, ...extractAnswerRowsFromQuestionsPayload(json)];
        merged = { ...merged, ...mergePrefillAnswers(combinedRows, json, flatSteps) };
        initialPrefillRef.current = merged;

        setSections(mapped);
        dispatch(setMedicalQuestionnaireTotalSections(mapped.length));
        setAnswers({ ...merged });
        setCompletedSteps(buildCompletedStepsFromAnswers(flatSteps, merged));
        const snaps = {};
        for (const s of flatSteps) {
          snaps[s.key] = stepPersistSnapshotFromAnswers(merged, s);
        }
        lastPersistedSnapshotByStepKeyRef.current = snaps;

        const resumeOneBased = resumeNextQuestionRef.current;
        if (resumeOneBased != null && flatSteps.length > 0) {
          const stepIndex = Math.max(
            0,
            Math.min(resumeOneBased - 1, flatSteps.length - 1)
          );
          setCurrentStep(stepIndex);
        }
      } catch (error) {
        const isRequestCancelled =
          controller.signal.aborted ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED';
        if (isRequestCancelled) return;
        setLoadError('Vragenlijst kon niet worden geladen.');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };
    loadQuestions();
    return () => controller.abort();
  }, [dispatch]);

  const active = steps[currentStep];
  const totalSteps = steps.length;
  const totalSections = sections.length;

  // useEffect(() => {
  //   setCurrentStep(nextQuestion);
  //   const nextAnswers = { ...initialPrefillRef.current };
  //   setAnswers(nextAnswers);
  //   setTouched({});
  //   setCompletedSteps({});
  //   const snaps = {};
  //   for (const s of steps) {
  //     snaps[s.key] = stepPersistSnapshotFromAnswers(nextAnswers, s);
  //   }
  //   lastPersistedSnapshotByStepKeyRef.current = snaps;
  // }, [totalSteps, nextQuestion]);
  const currentValue =
    active ? answers[active.key] ?? (active.multi ? [] : '') : '';
  const detailValue = active?.detailKey ? answers[active.detailKey] || '' : '';
  const hasFieldError = Boolean(fieldError);
  const fieldBorderClass = hasFieldError ? 'border-red-500' : 'border-[#CBD5E0]';

  const effectiveCompletedSteps = useMemo(
    () => ({
      ...buildCompletedStepsFromAnswers(steps, answers),
      ...completedSteps,
    }),
    [steps, answers, completedSteps]
  );

  flowStateRef.current = {
    currentStep,
    totalSteps,
    active,
    answers,
    sections,
    completedSteps: effectiveCompletedSteps,
    questionnaireCompleted,
    finalizeDoneRef: questionnaireFinalizeRef,
  };

  const completedSectionMap = useMemo(
    () => getSectionCompletionMap(sections, answers, effectiveCompletedSteps),
    [sections, answers, effectiveCompletedSteps]
  );
  const sectionNumber = (active?.sectionIdx || 0) + 1;
  const questionInSection = (active?.questionIdx || 0) + 1;
  const currentSection = active ? sections[active.sectionIdx] || sections[0] : null;
  const sectionTitle = currentSection?.title || active?.sectionTitle || '';
  const questionsInCurrentSection = currentSection?.questions?.length ?? 0;

  const activeChoiceOptionCount = useMemo(() => {
    if (!active || active.type !== 'choiceList') return 0;
    const choiceOptions =
      active.choiceOptions ||
      (active.options || []).map((label) => ({
        id: String(label),
        label: String(label),
      }));
    return choiceOptions.length;
  }, [active]);

  const choiceListOptionsWidthClass = useMemo(
    () =>
      activeChoiceOptionCount >= 9
        ? QUESTIONNAIRE_WIDE_OPTIONS_CLASS
        : QUESTIONNAIRE_FRAME_CLASS,
    [activeChoiceOptionCount]
  );

  useEffect(() => {
    if (!sections.length) return;
    syncOptimisticQuestionnaireProgress(
      dispatch,
      sections,
      answers,
      effectiveCompletedSteps
    );
  }, [dispatch, sections, answers, effectiveCompletedSteps]);

  useEffect(
    () => () => {
      void finalizeLastOptionalQuestionnaireStep(dispatch, flowStateRef.current).finally(
        () => {
          flushQuestionnaireProgressOnFlowExit(dispatch, store.getState);
        }
      );
    },
    [dispatch, store]
  );

  const handleNext = async () => {
    if (!active || isSubmitting) return;
    const isLast = currentStep === totalSteps - 1;
    const isLastOptional = isLast && active.optional;

    const requiredEmptyMessage = getStepRequiredEmptyMessage(
      { ...active, _detailValue: detailValue },
      currentValue,
      detailValue
    );
    if (requiredEmptyMessage) {
      setFieldError(requiredEmptyMessage);
      return;
    }

    const currentSnap = stepPersistSnapshotFromAnswers(answers, active);
    const refSnaps = lastPersistedSnapshotByStepKeyRef.current;
    const hasBaseline = Object.prototype.hasOwnProperty.call(refSnaps, active.key);
    const optionalWithoutInput = isOptionalStepWithoutInput(
      active,
      currentValue,
      detailValue
    );
    const skipSave =
      !isLastOptional &&
      !optionalWithoutInput &&
      hasBaseline &&
      refSnaps[active.key] === currentSnap;

    if (!skipSave) {
      setIsSubmitting(true);
    }
    try {
      let saveQuestionnaireFields = null;
      if (!skipSave) {
        const saveResult = await saveMedicalQuestionnaireStepAnswer(answers, active, {
          markQuestionnaireComplete: isLast,
        });
        saveQuestionnaireFields = saveResult?.questionnaireFields ?? null;
        lastPersistedSnapshotByStepKeyRef.current[active.key] = currentSnap;
      } else if (isLast) {
        persistMedicalQuestionnaireCompletedOnClient();
      }
      if (isLast) {
        questionnaireFinalizeRef.current = true;
      }
      await applyQuestionnaireProgressAfterSave(dispatch, saveQuestionnaireFields, {
        forceComplete: isLast,
        refetchProfile: !skipSave,
      });
      if (skipSave) {
        syncOptimisticQuestionnaireProgress(dispatch, sections, answers, effectiveCompletedSteps);
      }
    } catch (err) {
      if (!skipSave) {
        setIsSubmitting(false);
      }
      const validationDetail = parseMedicalQuestionnaireSaveValidationError(
        err,
        active.questionId
      );
      const code = err?.code;
      const msg = String(err?.message || '');
      if (validationDetail) {
        setFieldError(validationDetail);
      } else if (code === 'ECONNABORTED' || /timeout/i.test(msg)) {
        toast.error('Opslaan duurde te lang en is gestopt. Probeer het opnieuw.');
      } else {
        toast.error('Je antwoord kon niet worden opgeslagen. Probeer het opnieuw.');
      }
      return;
    }

    setFieldError('');
    setCompletedSteps((prev) => ({ ...prev, [active.key]: true }));
    if (isLast) {
      navigate(TEST_HISTORY_PATH);
    } else {
      setCurrentStep((s) => s + 1);
    }
    if (!skipSave) {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (!active) return;
    if (currentStep === 0) {
      navigate('/medical-questioning');
      return;
    }
    setCurrentStep((s) => s - 1);
  };

  if (loadError) {
    return (
      <div className="flex h-dvh max-h-dvh flex-col overflow-hidden bg-[#F9F5F2]">
        <div className="shrink-0">
          <MedicalQuestionnaireHeader />
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-4 py-7">
          <p className="font-poppins text-red-500 text-sm">{loadError}</p>
        </div>
      </div>
    );
  }

  if (isLoading || !active) {
    return (
      <div className="flex h-dvh max-h-dvh flex-col overflow-hidden bg-[#F9F5F2]">
        <div className="shrink-0">
          <MedicalQuestionnaireHeader />
        </div>
        <div className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-4 py-7">
          <p className="font-poppins text-[#6B7280] text-sm">Vragen worden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-dvh max-h-dvh flex-col overflow-hidden bg-[#F9F5F2]">
      <div className="shrink-0">
        <MedicalQuestionnaireHeader />
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <main className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-center overflow-x-hidden px-3 pb-6 sm:px-4 py-7 md:px-0">
          <div className="mx-auto flex h-[min(700px,calc(100dvh-7.5rem))] w-full min-w-0 min-h-0 shrink-0 flex-col items-center overflow-hidden sm:h-[min(700px,calc(100dvh-8rem))] md:h-[min(700px,calc(100dvh-9rem))]">
            <div className={`${QUESTIONNAIRE_FRAME_CLASS} pb-3 pt-4 sm:pb-3 sm:pt-4 md:pb-3 md:pt-4`}>
              <div
                className="mb-2 grid w-full min-w-0 gap-1"
                style={{ gridTemplateColumns: `repeat(${Math.max(sections.length, 1)}, minmax(0, 1fr))` }}
              >
                {sections.map((_, idx) => (
                  <div key={idx} className="min-w-0">
                    <div className="mb-1 flex h-3 items-center justify-center">
                      {completedSectionMap[idx] ? (
                        <img src={MedicalCheck} alt="" aria-hidden="true" className="h-4 w-4 object-contain" />
                      ) : null}
                    </div>
                    <div
                      className={`${PROGRESS_SEGMENT_CLASS} ${
                        idx < sectionNumber
                          ? 'border border-[#119975] bg-[#119975]'
                          : 'bg-[#DDE3E4]'
                      }`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between gap-2 text-[14px] font-poppins text-[#849094]">
                <span className="truncate">Sectie {sectionNumber} van {totalSections} - {sectionTitle}</span>
                <span className="font-semibold text-biowell-green">{Math.round(progressPct)}%</span>
              </div>
            </div>

            <div className="flex min-h-0 w-full flex-1 justify-center overflow-hidden">
              <section className="flex min-h-0 w-full min-w-0 flex-1 flex-col items-center justify-start gap-4 overflow-y-auto overscroll-y-contain px-0.5 pb-2 pt-10 sm:gap-5 sm:pt-14 md:pt-16 [-webkit-overflow-scrolling:touch]">
                  <h1
                    className={`${QUESTIONNAIRE_FRAME_CLASS} shrink-0 text-[#2B3133] leading-tight text-center font-libreBaskerville text-[28px] sm:text-[32px] md:text-[36px]`}
                  >
                    {active.question}
                  </h1>
                  <div
                    className={`w-full ${
                      active.type === 'yesNoWithTextarea'
                        ? QUESTIONNAIRE_FRAME_CLASS
                        : active.type === 'choiceList'
                          ? choiceListOptionsWidthClass
                          : 'mx-auto max-w-[335px]'
                    }`}
                  >
                    {active.type === 'yesNoWithTextarea' ? (
                      <div className="w-full">
                        {active.subQuestion ? (
                          <p className="text-center text-sm text-[#4B5563] font-poppins mb-4">
                            {active.subQuestion}
                          </p>
                        ) : null}
                        <div className="grid grid-cols-2 gap-2 w-full">
                          {(active.choiceOptions ||
                            (active.options || []).map((label) => ({
                              id: label,
                              label,
                            }))).map((opt) => {
                            const selected = currentValue === opt.id;
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => {
                                  setFieldError('');
                                  setAnswers((prev) => {
                                    const clearsDetail =
                                      active.detailKey &&
                                      !stepDetailInputVisible(active, opt.id);
                                    return {
                                      ...prev,
                                      [active.key]: opt.id,
                                      ...(clearsDetail ? { [active.detailKey]: '' } : {}),
                                    };
                                  });
                                }}
                                className={`w-full min-h-[48px] min-w-0 overflow-hidden rounded-lg border bg-white px-3 py-3 flex items-center justify-between gap-2 text-sm font-poppins transition-colors ${CHOICE_BUTTON_FOCUS_CLASS} ${selected ? 'border-[#119975] text-[#2B3133]' : 'border-[#CBD5E0] text-[#2B3133]'
                                  }`}
                              >
                                <span className="min-w-0 flex-1 text-left text-[15px] sm:text-[16px] leading-snug break-words">{opt.label}</span>
                                <ChoiceOptionIndicator selected={selected} />
                              </button>
                            );
                          })}
                        </div>
                        {stepDetailInputVisible(active, currentValue) ? (
                          <div className="mt-2.5 w-full text-left">
                            {active.detailLabel ? (
                              <p className="mb-1.5 font-poppins text-base font-medium text-[#2B3133]">
                                {active.detailLabel}
                              </p>
                            ) : null}
                            {active.detailHelperText ? (
                              <p className="mb-2.5 font-poppins text-sm leading-relaxed text-[#6F7B80]">
                                {active.detailHelperText}
                              </p>
                            ) : null}
                            <textarea
                              value={detailValue}
                              onChange={(e) => {
                                setFieldError('');
                                setAnswers((prev) => ({
                                  ...prev,
                                  [active.detailKey]: e.target.value,
                                }));
                              }}
                              placeholder={active.detailPlaceholder || ''}
                              aria-invalid={hasFieldError}
                              aria-label={active.detailLabel || undefined}
                              className={`w-full h-[86px] rounded-lg border bg-white px-3 py-2 text-sm font-poppins text-[#2B3133] placeholder:text-[#A7B0B3] outline-none focus:border-[#119975] resize-none ${fieldBorderClass}`}
                            />
                          </div>
                        ) : null}
                      </div>
                    ) : active.type === 'choiceList' ? (
                      (() => {
                        const choiceOptions =
                          active.choiceOptions ||
                          (active.options || []).map((label) => ({
                            id: String(label),
                            label: String(label),
                          }));
                        const optionCount = choiceOptions.length;
                        const gridColsClass =
                          optionCount >= 9
                            ? 'grid grid-cols-2 sm:grid-cols-3 gap-2'
                            : optionCount >= 4
                              ? 'grid grid-cols-1 sm:grid-cols-2 gap-2'
                              : 'grid grid-cols-1 gap-2';
                        const compact = optionCount >= 6;
                        const maxSelections = Number(active.maxSelections ?? active?.validation?.max);
                        const maxLimit =
                          Number.isFinite(maxSelections) && maxSelections > 0
                            ? maxSelections
                            : null;
                        return (
                          <div className={`${gridColsClass} w-full mx-auto`}>
                            {choiceOptions.map((opt) => {
                              const selected = active.multi
                                ? Array.isArray(currentValue) && currentValue.includes(opt.id)
                                : currentValue === opt.id;
                              return (
                                <button
                                  key={opt.id}
                                  type="button"
                                  onClick={() => {
                                    if (active.multi) {
                                      const existing = Array.isArray(answers[active.key])
                                        ? answers[active.key]
                                        : [];
                                      const isAlreadySelected = existing.includes(opt.id);
                                      if (
                                        !isAlreadySelected &&
                                        maxLimit != null &&
                                        existing.length >= maxLimit
                                      ) {
                                        setFieldError(maxSelectionLimitMessage(maxLimit));
                                        return;
                                      }
                                    }
                                    setFieldError('');
                                    setAnswers((prev) => {
                                      if (active.multi) {
                                        const existing = Array.isArray(prev[active.key])
                                          ? prev[active.key]
                                          : [];
                                        const isAlreadySelected = existing.includes(opt.id);
                                        if (
                                          !isAlreadySelected &&
                                          maxLimit != null &&
                                          existing.length >= maxLimit
                                        ) {
                                          return prev;
                                        }
                                        const next = isAlreadySelected
                                          ? existing.filter((item) => item !== opt.id)
                                          : [...existing, opt.id];
                                        return { ...prev, [active.key]: next };
                                      }
                                      return { ...prev, [active.key]: opt.id };
                                    });
                                  }}
                                  className={`w-full min-w-0 overflow-hidden ${CHOICE_BUTTON_FOCUS_CLASS} ${compact ? 'min-h-[44px] py-2.5 px-3 sm:px-4' : 'min-h-[48px] py-3 px-4'
                                    } rounded-xl border bg-white flex items-center justify-between gap-2 text-sm font-poppins transition-colors ${selected ? 'border-[#119975] text-[#2B3133]' : 'border-[#CBD5E0] text-[#2B3133]'
                                    }`}
                                >
                                  <span className={`min-w-0 flex-1 text-left leading-snug break-words ${compact ? 'text-[14px] sm:text-[15px]' : 'text-[15px] sm:text-[16px]'}`}>
                                    {opt.label}
                                  </span>
                                  <ChoiceOptionIndicator selected={selected} />
                                </button>
                              );
                            })}
                          </div>
                        );
                      })()
                    ) : active.type === 'select' ? (
                      <select
                        value={currentValue}
                        onChange={(e) => {
                          setFieldError('');
                          setAnswers((prev) => ({ ...prev, [active.key]: e.target.value }));
                        }}
                        aria-invalid={hasFieldError}
                        className={`${questionnaireInputClass(hasFieldError)} text-center`}
                      >
                        <option value="">{active.placeholder}</option>
                        {(active.options || []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    ) : active.type === 'number' ? (
                      <input
                        type="text"
                        inputMode={active?.validation?.integer ? 'numeric' : 'decimal'}
                        value={currentValue}
                        onChange={(e) => {
                          setFieldError('');
                          setAnswers((prev) => ({
                            ...prev,
                            [active.key]: sanitizeNumericInput(
                              e.target.value,
                              active?.validation?.integer !== true
                            ),
                          }));
                        }}
                        placeholder={active.placeholder}
                        aria-invalid={hasFieldError}
                        className={`${questionnaireInputClass(hasFieldError)} text-center`}
                      />
                    ) : (
                      <input
                        type={active.type}
                        value={currentValue}
                        onChange={(e) => {
                          setFieldError('');
                          setAnswers((prev) => ({ ...prev, [active.key]: e.target.value }));
                        }}
                        placeholder={active.placeholder}
                        aria-invalid={hasFieldError}
                        className={`${questionnaireInputClass(hasFieldError)} text-center`}
                      />
                    )}
                    {fieldError ? (
                      <p className="text-center text-xs text-red-500 font-poppins mt-2" role="alert">
                        {fieldError}
                      </p>
                    ) : null}
                  </div>
              </section>
            </div>

            <footer className={`${QUESTIONNAIRE_FRAME_CLASS} border-t border-[#E8ECEE] bg-[#F9F5F2] pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3`}>
                  <div className="w-full flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between">
                    <p className="text-[14px] text-[#8A9498] font-poppins text-center md:text-left">
                      Vraag {questionInSection} van {questionsInCurrentSection} in deze sectie
                    </p>
                    <div className="flex items-center justify-center md:justify-end gap-2.5">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={isSubmitting}
                        className="h-10 px-6 rounded-full bg-[#EFF1F2] text-[#8A9498] font-poppins text-sm font-medium hover:bg-[#E4E8EA] transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        Vorige
                      </button>
                      <button
                        type="button"
                        onClick={handleNext}
                        disabled={isSubmitting}
                        className="h-10 px-7 rounded-full bg-[#119975] text-white font-poppins text-sm font-medium hover:bg-[#0f8a6b] transition-colors disabled:cursor-not-allowed disabled:opacity-70"
                      >
                        {isSubmitting
                          ? 'Opslaan…'
                          : currentStep === totalSteps - 1
                            ? 'Afronden'
                            : 'Volgende'}
                      </button>
                    </div>
                  </div>
            </footer>
          </div>
        </main>
      </div>
    </div>
  );
}
